/*
  <advent-plans> — up to three flat-priced purchase options on a single variant.

  Deliberately thinner than product-plans.js: prices arrive pre-formatted from Liquid (so the
  market's currency format is respected), every option is a single cart line, and there is no
  servings/bulk-price maths. What's left is selection, the CTA + sticky mirror, and the add.

  Each option carries its own selling plan, resolved server-side, so two plans with the same
  price and interval stay distinguishable in the cart and downstream in the subscription app.
*/
(function () {
  class AdventPlans extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-advent-data]');
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        this.data = { variants: {}, selected: null };
      }

      this.soldOutLabel = this.getAttribute('data-soldout-label') || 'Sold out';
      this.cards = Array.from(this.querySelectorAll('.plan-card'));
      this.cta = this.querySelector('[data-advent-cta]');
      this.currentId = this.data.selected;

      // The preferred option starts as the configured default and updates only on an explicit
      // shopper choice. render() restores it whenever it becomes visible again rather than
      // falling back one-way — a one-way fallback silently loses the configured default.
      this.preferredOption = this.getAttribute('data-default-option') || '1';

      // Sticky bar: move to <body> so position:fixed isn't broken by a transformed ancestor
      // (the product-info reveal animation is one), after purging any stale copy this block
      // left behind on a previous connect.
      this.stickyBar = this.querySelector('[data-advent-sticky]');
      if (this.stickyBar) {
        const owner = this.stickyBar.getAttribute('data-owner');
        if (owner) {
          document.querySelectorAll(`body > [data-advent-sticky][data-owner="${owner}"]`).forEach((el) => el.remove());
        }
        document.body.appendChild(this.stickyBar);
        this.stickyCta = this.stickyBar.querySelector('[data-advent-sticky-cta]');
        if (this.stickyCta) this.stickyCta.addEventListener('click', () => this.attemptAddToCart());
        this.initStickyObserver();
      }

      // Repeat starter-claim warning, same treatment and same cookie key as the plans block.
      this.modal = this.querySelector('[data-advent-modal]');
      if (this.modal) {
        const mOwner = this.modal.getAttribute('data-owner');
        if (mOwner) {
          document.querySelectorAll(`body > [data-advent-modal][data-owner="${mOwner}"]`).forEach((el) => el.remove());
        }
        document.body.appendChild(this.modal);
        this.modal.querySelectorAll('[data-advent-modal-close]').forEach((el) =>
          el.addEventListener('click', () => this.closeModal())
        );
        const cont = this.modal.querySelector('[data-advent-modal-continue]');
        if (cont) cont.addEventListener('click', () => { this.closeModal(); this.addToCart(); });
      }

      this.cards.forEach((card) => {
        const radio = card.querySelector('.plan-radio');
        if (!radio) return;
        radio.addEventListener('change', () => {
          this.preferredOption = card.getAttribute('data-option');
          this.selectCard(card);
        });
      });

      if (this.cta) this.cta.addEventListener('click', () => this.attemptAddToCart());

      // Single-variant products never fire this; it's here so the block stays correct if
      // flavours are added later.
      this.onVariantChange = (event) => {
        if (event.detail && event.detail.variant) {
          this.currentId = String(event.detail.variant.id);
          this.render();
        }
      };
      (this.productForm() || document).addEventListener('variant:change', this.onVariantChange);

      this.render();
    }

    disconnectedCallback() {
      (this.productForm() || document).removeEventListener('variant:change', this.onVariantChange);
      this.stickyObserver?.disconnect();
      // Both were relocated to <body>, so they have to be removed explicitly.
      this.stickyBar?.remove();
      this.modal?.remove();
    }

    productForm() {
      const id = this.getAttribute('data-product-form-id');
      return id ? document.getElementById(id) : null;
    }

    get variant() {
      return this.data.variants[this.currentId];
    }

    // Pricing + selling plan for one option on the current variant.
    optionData(option) {
      const v = this.variant;
      return v && v.options ? v.options[option] : null;
    }

    get activeOption() {
      const checked = this.querySelector('.plan-radio:checked');
      return checked ? checked.value : this.preferredOption;
    }

    get activeCard() {
      return this.cards.find((c) => c.getAttribute('data-option') === this.activeOption) || null;
    }

    // --- Repeat starter-claim warning (soft, cookie-based) ---

    getCookie(name) {
      const value = '; ' + document.cookie;
      const parts = value.split('; ' + name + '=');
      if (parts.length === 2) return parts.pop().split(';').shift();
      return '';
    }

    setCookie(name, value, days) {
      let expires = '';
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + value + expires + '; path=/; SameSite=Lax';
    }

    // Written to BOTH localStorage and a cookie, and either counts as claimed. The cookie is
    // shared with the plans block and the multi-step starter flow; localStorage is the reliable
    // path inside the theme editor, where third-party cookie writes are blocked.
    starterClaimed() {
      try {
        if (window.localStorage && localStorage.getItem('starter_pack_claimed') === '1') return true;
      } catch (e) {}
      return this.getCookie('starter_pack_claimed') === '1';
    }

    setStarterClaimed() {
      try {
        if (window.localStorage) localStorage.setItem('starter_pack_claimed', '1');
      } catch (e) {}
      this.setCookie('starter_pack_claimed', '1', 365);
    }

    openModal() {
      if (this.modal) this.modal.removeAttribute('hidden');
    }

    closeModal() {
      if (this.modal) this.modal.setAttribute('hidden', '');
    }

    // Only options that show a gift list are "starter pack" claims worth warning about.
    activeClaimsStarter() {
      const card = this.activeCard;
      return !!(card && card.querySelector('.plan-card__items'));
    }

    attemptAddToCart() {
      if (this.modal && this.activeClaimsStarter() && this.starterClaimed()) {
        this.openModal();
        return;
      }
      this.addToCart();
    }

    initStickyObserver() {
      if (!this.cta || !this.stickyBar || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(
        (entries) => {
          // "Passed" means the CTA is above the viewport top — not merely below the fold on
          // page load, before the shopper has reached it.
          this._ctaPassed = entries[0].boundingClientRect.bottom <= 0;
          this.syncSticky();
        },
        { threshold: 0 }
      );
      this.stickyObserver.observe(this.cta);
    }

    syncSticky() {
      if (!this.stickyBar) return;
      const v = this.variant;
      const soldOut = !v || !v.available;
      const show = !!this._ctaPassed && !soldOut;
      this.stickyBar.classList.toggle('is-visible', show);
      this.stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    selectCard(card) {
      this.cards.forEach((c) => c.classList.toggle('is-selected', c === card));
      this.updateCta();
    }

    setPrice(priceEl, compareEl, data) {
      if (priceEl) priceEl.textContent = data ? data.price_display : '';
      if (!compareEl) return;
      if (data && data.compare > data.price) {
        compareEl.textContent = data.compare_display;
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.setAttribute('hidden', '');
      }
    }

    updateCta() {
      const v = this.variant;
      const soldOut = !v || !v.available;
      const option = this.activeOption;
      const od = this.optionData(option);
      const card = this.activeCard;

      this.setPrice(this.querySelector('[data-cta-price]'), this.querySelector('[data-cta-compare]'), od);

      // Option-specific note: show only the selected option's.
      this.querySelectorAll('[data-option-note]').forEach((el) => {
        el.hidden = el.getAttribute('data-option-note') !== option;
      });
      const subNote = this.querySelector('[data-cta-sub-note]');
      if (subNote) subNote.hidden = !card || card.getAttribute('data-purchase-type') !== 'subscription';

      // Express payment buttons: keep the variant and selling plan in step with the selection.
      const paymentWrap = this.querySelector('[data-advent-payment]');
      if (paymentWrap) {
        const idInput = paymentWrap.querySelector('[data-payment-id]');
        const planInput = paymentWrap.querySelector('[data-payment-plan]');
        if (idInput) idInput.value = this.currentId;
        if (planInput) planInput.value = od && od.plan ? od.plan : '';
        paymentWrap.hidden = soldOut;
      }

      if (this.cta) {
        this.cta.classList.toggle('plans-cta--soldout', soldOut);
        this.cta.disabled = soldOut;
        const labelEl = this.cta.querySelector('.plans-cta__label');
        if (labelEl) {
          if (labelEl.dataset.tmpl === undefined) labelEl.dataset.tmpl = labelEl.textContent;
          labelEl.textContent = soldOut ? this.soldOutLabel : labelEl.dataset.tmpl;
        }
        const priceEl = this.querySelector('[data-cta-price]');
        const compareEl = this.querySelector('[data-cta-compare]');
        if (priceEl) priceEl.hidden = soldOut;
        if (compareEl && soldOut) compareEl.setAttribute('hidden', '');
      }

      // Mirror onto the sticky bar.
      if (this.stickyBar) {
        const sTitle = this.stickyBar.querySelector('[data-sticky-option-title]');
        if (sTitle && card) {
          const titleEl = card.querySelector('[data-option-title]');
          if (titleEl) sTitle.textContent = titleEl.textContent;
        }
        const sPrice = this.stickyBar.querySelector('[data-sticky-price]');
        const sCompare = this.stickyBar.querySelector('[data-sticky-compare]');
        this.setPrice(sPrice, sCompare, od);
        if (this.stickyCta) {
          this.stickyCta.classList.toggle('plans-sticky__cta--soldout', soldOut);
          this.stickyCta.disabled = soldOut;
        }
        const sLabel = this.stickyBar.querySelector('[data-sticky-label]');
        if (sLabel) {
          if (sLabel.dataset.tmpl === undefined) sLabel.dataset.tmpl = sLabel.textContent;
          sLabel.textContent = soldOut ? this.soldOutLabel : sLabel.dataset.tmpl;
        }
        if (sPrice) sPrice.hidden = soldOut;
        if (sCompare && soldOut) sCompare.setAttribute('hidden', '');
        this.syncSticky();
      }
    }

    render() {
      const v = this.variant;
      const soldOut = !v || !v.available;

      this.cards.forEach((card) => {
        const option = card.getAttribute('data-option');
        const od = this.optionData(option);
        const radio = card.querySelector('.plan-radio');
        const soldOutBadge = card.querySelector('[data-option-soldout]');

        card.classList.toggle('plan-card--soldout', soldOut);
        if (radio) radio.disabled = soldOut;
        if (soldOutBadge) soldOutBadge.toggleAttribute('hidden', !soldOut);

        // Sold out: keep every card visible but disabled and badged. Otherwise hide a
        // subscription option that has no selling plan on this variant — it can't be bought.
        if (soldOut) {
          card.removeAttribute('hidden');
          if (!v) return;
        } else if (!od || (card.getAttribute('data-purchase-type') === 'subscription' && !od.plan)) {
          card.setAttribute('hidden', '');
          return;
        }

        card.removeAttribute('hidden');

        this.setPrice(card.querySelector('[data-option-price]'), card.querySelector('[data-option-compare]'), od);

        const saveEl = card.querySelector('[data-option-save]');
        if (saveEl && od) {
          if (od.compare > od.price) {
            const prefix = saveEl.getAttribute('data-save-label') || '';
            saveEl.textContent = (prefix ? prefix + ' ' : '') + od.saving_display;
            saveEl.removeAttribute('hidden');
          } else {
            saveEl.setAttribute('hidden', '');
          }
        }
      });

      // Preferred option when it's visible; else keep the current visible selection; else the
      // first visible card. This restores the default once it becomes available again instead
      // of sticking on a one-way fallback.
      const isVisible = (c) => c && !c.hasAttribute('hidden');
      let target = this.cards.find((c) => c.getAttribute('data-option') === this.preferredOption);
      if (!isVisible(target)) {
        const current = this.querySelector('.plan-card.is-selected');
        target = isVisible(current) ? current : this.cards.find(isVisible);
      }
      if (target) {
        const radio = target.querySelector('.plan-radio');
        if (radio && !radio.checked) radio.checked = true;
        if (!target.classList.contains('is-selected')) this.selectCard(target);
      }

      this.updateCta();
    }

    setLoading(on) {
      [this.cta, this.stickyCta].forEach((btn) => {
        if (!btn) return;
        btn.toggleAttribute('aria-busy', on);
        btn.disabled = on;
      });
    }

    addToCart() {
      const v = this.variant;
      if (!v) return;
      const card = this.activeCard;
      const od = this.optionData(this.activeOption);
      if (!card || !od) return;

      // Record the claim only when the merchant opted into the shared bookkeeping (the warning
      // is configured). The key is read by the plans block and the multi-step starter flow, so
      // writing it unconditionally would change their behaviour from a block that never asked.
      if (this.modal && this.activeClaimsStarter()) this.setStarterClaimed();

      // One line, always. The gift list on an option is display only — the subscription app
      // adds those products to a later order, driven by the selling plan below.
      const item = { id: Number(this.currentId), quantity: 1 };
      if (card.getAttribute('data-purchase-type') === 'subscription' && od.plan) {
        item.selling_plan = Number(od.plan);
      }
      const props = {};
      const propKey = card.getAttribute('data-property-key');
      if (propKey) props[propKey] = card.getAttribute('data-property-value') || '';

      // Gift-list options carry the pack's value so the cart can draw a pseudo-row for it —
      // nothing extra is added to the cart, this is presentation data travelling on the line.
      // Underscore-prefixed, so Shopify keeps them out of checkout and the confirmation email
      // while the cart (and admin) can still read them. `_advent_gift_prop` names the visible
      // property the row replaces, so the cart can suppress that text line and avoid showing
      // the same thing twice; checkout keeps it, being all Shopify will render there.
      const giftValue = card.getAttribute('data-gift-value');
      if (giftValue) {
        props._advent_gift_value = giftValue;
        if (propKey) props._advent_gift_prop = propKey;
        const giftImage = card.getAttribute('data-gift-image');
        if (giftImage) props._advent_gift_image = giftImage;
      }

      if (Object.keys(props).length) item.properties = props;

      const routes = (window.theme && theme.routes) || {};
      const addUrl = routes.cart_add_url || '/cart/add';
      const cartUrl = routes.cart_url || '/cart';

      // Gather sections to render — the cart drawer registers its own section id.
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      this.setLoading(true);
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sectionsToBundle, sections_url: window.location.pathname }),
      })
        .then((r) => r.json())
        .then(async (res) => {
          if (res && res.status) {
            this.setLoading(false);
            console.warn('Advent plans add to cart error:', res.description || res.message);
            return;
          }

          // On the cart page there's no drawer — go to the cart.
          if (
            document.body.classList.contains('template-cart') ||
            (window.theme && theme.settings && theme.settings.cartType === 'page')
          ) {
            window.location.href = cartUrl;
            return;
          }

          // Refresh the cart UI (count + drawer contents) and open the drawer, like a normal add.
          let cartJson = null;
          try {
            cartJson = await (await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })).json();
            cartJson.sections = res.sections;
          } catch (e) {}
          if (window.theme && theme.pubsub && cartJson) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'advent-plans', cart: cartJson });
          }

          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.show === 'function') {
            drawer.show();
            this.setLoading(false);
          } else {
            window.location.href = cartUrl;
          }
        })
        .catch(() => this.setLoading(false));
    }
  }

  if (!customElements.get('advent-plans')) {
    customElements.define('advent-plans', AdventPlans);
  }
})();
